/**
 * <candle-preview> Web Component
 *
 * Composites a user-provided image and engraved text on top of a static
 * candle background image. Uses Shadow DOM for style encapsulation and
 * scales responsively via ResizeObserver.
 *
 * Attributes:
 *   data-static-url    – URL of the static candle background image
 *   data-image-url     – URL of the user-provided image (1:1 aspect ratio)
 *   data-engraved-text – Text to engrave on the candle
 *   data-font-name     – Engraving font key: mono, victor, or cutive
 *
 * Configurable CSS custom properties (set on the element via style=""):
 *   --image-top        – vertical offset of the provided image (default: 20%)
 *   --image-width      – width (and height, 1:1) of the provided image (default: 50%)
 *   --text-top         – vertical offset of the text within the static background (default: 70%)
 *   --text-max-width   – max width of the text block relative to the static background (default: 80%)
 *   --text-font-size   – base font size of the engraved text (default: 16px)
 */

const NATURAL_WIDTH = 600; // Reference width in px – at this width, no scaling is applied
const MIN_TEXT_FONT_SIZE = 11;
const TEXT_FIT_PRECISION = 0.25;
const MAX_TEXT_LINES = 2;
const ELLIPSIS = '...';
const FONT_FAMILY_BY_NAME = {
  mono: 'monospace',
  victor: '"Victor Mono", monospace',
  cutive: '"Cutive Mono", monospace',
};

class CandlePreview extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | null} */
  #fitFrame = null;

  static get observedAttributes() {
    return ['data-static-url', 'data-image-url', 'data-engraved-text', 'data-disclaimer', 'data-font-name'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.refresh();

    if (!this.#resizeObserver) {
      this.#setupResizeObserver();
    }
  }

  disconnectedCallback() {
    if (this.#fitFrame !== null) {
      cancelAnimationFrame(this.#fitFrame);
      this.#fitFrame = null;
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  attributeChangedCallback() {
    if (!this.isConnected) {
      return;
    }

    this.refresh();
  }

  refresh() {
    this.#render();
  }

  #render() {
    const staticUrl = this.dataset.staticUrl || '';
    const imageUrl = this.dataset.imageUrl || '';
    const engravedText = this.dataset.engravedText || '';
    const disclaimer = this.dataset.disclaimer || '';
    const fontName = this.dataset.fontName || 'mono';
    const fontFamily = FONT_FAMILY_BY_NAME[fontName] || FONT_FAMILY_BY_NAME.mono;

    const shadowRoot = /** @type {ShadowRoot} */ (this.shadowRoot);

    shadowRoot.innerHTML = `
      <style>
        :host {
          /* Configurable root values */
          --image-top: 12.5%;
          --image-width: 58%;
          --image-width-no-text: 52%;
          --text-top: 58%;
          --text-max-width: 42%;
          --text-font-size: 28px;
          --text-color: white;
          --provided-image-object-fit: cover;
          --provided-image-filter: none;

          /* Internal scale factor – updated by ResizeObserver */
          --_scale: 1;

          display: block;
          width: 100%;
        }

        .wrapper {
          position: relative;
          width: 100%;
          overflow: hidden;
        }

        .static-bg {
          display: block;
          width: 100%;
          height: auto;
        }

        .image-container {
          position: absolute;
          top: var(--image-top);
          left: 50%;
          transform: translateX(-50%);
          width: var(--image-width);
        }

        /* When no text is provided, center the image vertically and hide the text overlay */
        .wrapper.no-text .image-container {
          top: 50%;
          transform: translate(-50%, -60%);
          width: var(--image-width-no-text);
        }

        .wrapper.no-text .text-overlay {
          display: none;
        }

        /* When no image is provided, center the text vertically and hide the image */
        .wrapper.no-image .image-container {
          display: none;
        }

        .wrapper.no-image .text-overlay {
          top: 50%;
          transform: translate(-50%, -50%) perspective(500px) rotateX(15deg);
        }

        .provided-image {
          display: block;
          width: 100%;
          aspect-ratio: 1.28;
          object-fit: contain;
          filter: var(--provided-image-filter);
        }

        .disclaimer {
          position: absolute;
          bottom: 3%;
          left: 50%;
          transform: translateX(-50%);
          width: 90%;
          text-align: center;
          font-size: calc(32px * var(--_scale));
          color: rgba(255, 255, 255, 0.85);
          font-style: italic;
          pointer-events: none;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .text-overlay {
          position: absolute;
          top: var(--text-top);
          left: 50%;
          transform: translateX(-50%) perspective(500px) rotateX(15deg);
          width: var(--text-max-width);

          font-family: ${fontFamily};
          font-size: calc(var(--text-font-size) * var(--_scale));
          line-height: 1.3;
          text-align: center;
          color: var(--text-color);

          display: block;
          overflow: hidden;

          /* Only explicit line breaks should create a new line */
          word-break: normal;
          overflow-wrap: normal;
          white-space: pre;

          letter-spacing: -0.04em;
          font-weight: 600;
        }

        .text-measurer {
          position: absolute;
          top: 0;
          left: -9999px;
          display: inline-block;
          width: max-content;
          visibility: hidden;
          pointer-events: none;

          font-family: ${fontFamily};
          line-height: 1.3;
          letter-spacing: -0.04em;
          font-weight: 600;
          white-space: pre;
        }
      </style>

      <div class="wrapper${engravedText ? '' : ' no-text'}${imageUrl ? '' : ' no-image'}">
        <img class="static-bg" src="${staticUrl}" alt="" />

        <div class="image-container">
          <img class="provided-image" src="${imageUrl}" alt="" />
        </div>

        <div class="text-overlay"></div>
        <div class="text-measurer" aria-hidden="true"></div>
        ${disclaimer ? `<div class="disclaimer">${disclaimer}</div>` : ''}
      </div>
    `;

    const textOverlay = shadowRoot.querySelector('.text-overlay');
    if (textOverlay) {
      textOverlay.textContent = engravedText;
    }

    this.#queueTextFit();
  }

  #setupResizeObserver() {
    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const availableWidth = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        const scale = Math.min(1, availableWidth / NATURAL_WIDTH);
        this.style.setProperty('--_scale', `${scale}`);
        this.#queueTextFit();
      }
    });

    /** @type {ResizeObserver} */
    const resizeObserver = this.#resizeObserver;

    resizeObserver.observe(this);
  }

  #queueTextFit() {
    if (this.#fitFrame !== null) {
      cancelAnimationFrame(this.#fitFrame);
    }

    this.#fitFrame = requestAnimationFrame(() => {
      this.#fitFrame = null;
      this.#fitEngravedText();
    });
  }

  #fitEngravedText() {
    const shadowRoot = /** @type {ShadowRoot | null} */ (this.shadowRoot);
    const textOverlay = shadowRoot?.querySelector('.text-overlay');
    const textMeasurer = shadowRoot?.querySelector('.text-measurer');

    if (!(textOverlay instanceof HTMLElement) || !(textMeasurer instanceof HTMLElement)) {
      return;
    }

    const engravedText = this.dataset.engravedText || '';

    textOverlay.style.removeProperty('font-size');
    textOverlay.textContent = engravedText;

    if (!engravedText.trim()) {
      return;
    }

    const availableWidth = textOverlay.clientWidth;
    if (!availableWidth) {
      return;
    }

    const computedStyles = getComputedStyle(textOverlay);
    const baseFontSize = parseFloat(computedStyles.fontSize);

    if (!Number.isFinite(baseFontSize) || baseFontSize <= 0) {
      return;
    }

    const sourceLines = engravedText.split(/\r?\n/);
    const hasExplicitLineBreaks = sourceLines.length > 1;

    const measureLineWidth = (line, fontSize) => {
      textMeasurer.style.fontSize = `${fontSize}px`;
      textMeasurer.textContent = line || ' ';
      return textMeasurer.getBoundingClientRect().width;
    };

    const lineFits = (line, fontSize) => measureLineWidth(line, fontSize) <= availableWidth + 1;

    const getBestTwoLineSplit = (line, fontSize) => {
      const trimmedLine = line.trim();

      if (!trimmedLine || lineFits(trimmedLine, fontSize)) {
        return [trimmedLine];
      }

      const words = trimmedLine.split(/\s+/).filter(Boolean);
      const useWordBoundaries = words.length > 1;
      const segments = useWordBoundaries ? words : Array.from(trimmedLine);
      const joiner = useWordBoundaries ? ' ' : '';

      if (segments.length < 2) {
        return [trimmedLine];
      }

      let bestSplit = null;

      for (let index = 1; index < segments.length; index += 1) {
        const firstLine = segments.slice(0, index).join(joiner).trim();
        const secondLine = segments.slice(index).join(joiner).trim();

        if (!firstLine || !secondLine) {
          continue;
        }

        const firstWidth = measureLineWidth(firstLine, fontSize);
        const secondWidth = measureLineWidth(secondLine, fontSize);
        const candidate = {
          lines: [firstLine, secondLine],
          maxWidth: Math.max(firstWidth, secondWidth),
          balance: Math.abs(firstWidth - secondWidth),
        };

        if (
          !bestSplit ||
          candidate.maxWidth < bestSplit.maxWidth ||
          (candidate.maxWidth === bestSplit.maxWidth && candidate.balance < bestSplit.balance)
        ) {
          bestSplit = candidate;
        }
      }

      return bestSplit?.lines || [trimmedLine];
    };

    const getVisibleLayout = (fontSize) => {
      if (hasExplicitLineBreaks) {
        return {
          lines: sourceLines.slice(0, MAX_TEXT_LINES),
          hasHiddenLines: sourceLines.length > MAX_TEXT_LINES,
        };
      }

      return {
        lines: getBestTwoLineSplit(sourceLines[0] || '', fontSize).slice(0, MAX_TEXT_LINES),
        hasHiddenLines: false,
      };
    };

    const truncateLineToWidth = (line, fontSize, forceEllipsis = false) => {
      if (!line && !forceEllipsis) {
        return '';
      }

      if (!forceEllipsis && lineFits(line, fontSize)) {
        return line;
      }

      let end = line.length;
      while (end > 0) {
        const candidate = `${line.slice(0, end).trimEnd()}${ELLIPSIS}`;
        if (lineFits(candidate, fontSize)) {
          return candidate;
        }
        end -= 1;
      }

      return lineFits(ELLIPSIS, fontSize) ? ELLIPSIS : '';
    };

    const renderLinesAtFontSize = (fontSize) => {
      const { lines, hasHiddenLines } = getVisibleLayout(fontSize);
      const renderedLines = lines.map((line, index) => {
        if (hasHiddenLines && index === MAX_TEXT_LINES - 1) {
          return truncateLineToWidth(line, fontSize, true);
        }

        return truncateLineToWidth(line, fontSize);
      });

      return renderedLines;
    };

    const fitsVisibleLines = (fontSize) => {
      return getVisibleLayout(fontSize).lines.every((line) => lineFits(line, fontSize));
    };

    const minFontSize = Math.min(MIN_TEXT_FONT_SIZE, baseFontSize);
    const { lines: minSizeLines, hasHiddenLines: minSizeHasHiddenLines } = getVisibleLayout(minFontSize);
    const needsTruncationAtMinSize =
      minSizeHasHiddenLines || minSizeLines.some((line) => !lineFits(line, minFontSize));

    if (!needsTruncationAtMinSize && fitsVisibleLines(baseFontSize)) {
      const renderedLines = getVisibleLayout(baseFontSize).lines;

      textOverlay.textContent = renderedLines.join('\n');
      return;
    }

    let low = minFontSize;
    let high = baseFontSize;
    let bestFit = low;

    if (!needsTruncationAtMinSize) {
      while ((high - low) > TEXT_FIT_PRECISION) {
        const mid = (low + high) / 2;

        if (fitsVisibleLines(mid)) {
          bestFit = mid;
          low = mid;
        } else {
          high = mid;
        }
      }

      textOverlay.style.fontSize = `${bestFit}px`;
      const renderedLines = getVisibleLayout(bestFit).lines;

      textOverlay.textContent = renderedLines.join('\n');
      return;
    }

    textOverlay.style.fontSize = `${minFontSize}px`;
    const renderedLines = renderLinesAtFontSize(minFontSize);

    textOverlay.textContent = renderedLines.join('\n');
  }
}

if (!customElements.get('candle-preview')) {
  customElements.define('candle-preview', CandlePreview);
}
